import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { io, Socket } from 'socket.io-client';
import { URL } from 'iso-url';
import {
  CanaryResults,
  CLIENT_UPGRADE_REQUIRED,
  ComparionAgent,
  Flow,
  PIGEON_PROTOCOL_VERSION,
  TriggerManualParseWsMessage,
  WebsocketMessageType,
  WebsocketMessageTypeFactory,
} from '../../types/pigeon';
import {
  Form,
  LineType,
  ArtifactType,
  ObjectUrl,
  CarrierPageStateResponse,
  KeepAliveResponse,
  CarrierPageState,
  TrainingData,
} from '../../types';
import { ValidationLevel } from '../developer';
import { WebClientStorage } from '../owl';
import { Carrier } from '../../owl';

export type Slug = string;

export interface CarrierImplementationData {
  slug: Slug;
  validationLevel: ValidationLevel;
  hasAutomation: boolean;
  /** the carrier sometimes requires mfa to login */
  hasMfa: boolean;
  /** We have written validation to automate mfa */
  mfaImplemented: boolean;
  fullStoryEnabled: boolean;
  keepAliveEnabled: boolean;
}
export interface ClientValidationState {
  /** INS-1391: owner generation that emitted this state */
  generation?: number;
  /** Identifies the current user-input challenge within a generation. */
  challengeId?: string;
  /** INS-1391: present after a lost browser was replaced by a recovery login */
  recoveryNoticeId?: string;
  recoveryNotice?: string;
  recoveryNumber?: number;
  recoveryPreviousFlow?: Flow;
  /** Pigeon-internal: a recovery login is in progress */
  recovering?: boolean;
  flow: Flow;
  form?: Form;
  carrierSlug: string;
  /** Is set once the first login attempt happens */
  submissionId: string;
  clientId?: string;
  lineType: LineType;
  /** If there was an error while processing a form-result it is set here */
  errorMessage?: string;
  /** Set once validation is done so the client can show the success screen */
  validationOver: boolean;
  /** Set if mfa is required but we don't know how to handle it right now**/
  manualMfa?: boolean;

  /** The attempt number. Every time that invalid credentials are given a new login attempt
   * is created with this property incremented by one.
   */
  attemptNumber: number;

  /* True when the flow is actively running */
  isRunning: boolean;
  hasMfa: boolean;
  /* True when validation fails in MFA_OPTION_SELECT or MFA_CODE_ENTRY */
  mfaErrored: boolean;
  unknownCredentials: boolean;
}

export interface StartValidationAttemptWsMessage {
  clientId: string;
  carrierSlug: string;
  lineType: LineType;
  submissionId: string;
}

export type CreateValidationAttemptWsMessage = StartValidationAttemptWsMessage;

export type GetValidationStateWsMessage = ValidationAccessWsMessage;

/** INS-1391: every validation-scoped message carries the capability and its binding. */
export interface ValidationAccessWsMessage {
  submissionId: string;
  capability: string;
  clientId: string;
  carrierSlug: string;
  lineType: LineType;
}

export type AbortValidationWsMessage = ValidationAccessWsMessage;

export interface ValidationCommandWsMessage extends ValidationAccessWsMessage {
  requestId: string;
  expectedGeneration: number;
  expectedChallengeId?: string;
  data: Record<string, string>;
}

export type ValidationCommandStatus =
  | 'accepted'
  | 'completed'
  | 'retryable'
  | 'not_found'
  | 'restart_required'
  | 'stale_challenge';

export interface ValidationCommandAck {
  status: ValidationCommandStatus;
  requestId: string;
}

export interface GetCarrierLoginFormWsMessage {
  carrier: Carrier;
  lineType: LineType;
}

export interface FormDataWsMessage {
  submissionId?: string;
  clientId: string;
  data: Record<string, string>;
}

type PresignedPostFields = {
  [key: string]: string;
};

export interface PresignedPost {
  url: string;
  fields: PresignedPostFields;
}

export enum PigeonS3ObjectType {
  Trace = 'trace',
  Har = 'har',
}

export const PIGEON_ACCESS_TOKEN_STORAGE_KEY = 'pigeon-access-token';

export class PigeonClient {
  public client: AxiosInstance;
  protected socket: Socket | null = null;

  //============================
  //      Helper Functions
  //============================

  private createTwoWaySocketMessage<T, S>(
    messageName: string,
  ): (data: T) => Promise<S> {
    return (data: T) => {
      return new Promise((resolve, reject) => {
        if (!this.socket) {
          reject(new Error('Not connected to Pigeon'));
          return;
        }
        this.socket
          .timeout(10_000)
          .emit(messageName, data, (error: Error | null, response: S) => {
            if (error) reject(error);
            else resolve(response);
          });
      });
    };
  }

  /**
   * Create a pigeon client
   * @param baseUrl the base http url. Do not include a trailing slash. This url will also be used to connect to pigeon's websocket.
   */
  constructor(
    private baseUrl: string,
    private storage: WebClientStorage,
    /** INS-1391: the developer CLI's Owl token, sent in the socket handshake */
    private serviceToken?: () =>
      | string
      | null
      | undefined
      | Promise<string | null | undefined>,
  ) {
    this.client = axios.create({ baseURL: baseUrl });
  }

  async getCanaryResults(
    from: number,
    to: number,
    carrier: string,
  ): Promise<CanaryResults | void> {
    const data = await this.client.get<CanaryResults>(
      `/canary-results/${from}/${to}/${carrier}`,
    );
    return data.data;
  }

  async getTrainingData(): Promise<TrainingData> {
    const data = await this.client.get<TrainingData>(`/training-data`);
    return data.data;
  }

  async getBucketObjects(
    submissionId: string,
    type?: ArtifactType.HAR | ArtifactType.Trace,
  ): Promise<ObjectUrl[]> {
    const accessToken = this.storage.getItem('access-token');
    const { data } = await this.client.get<ObjectUrl[]>(
      `/artifact/${submissionId}/${type ?? ArtifactType.Trace}`,
      {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return data;
  }

  /** Converts the baseurl into a websocket url */
  private get socketUrl() {
    const url = new URL(this.baseUrl);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}`;
  }

  disconnectFromSocket() {
    this.socket?.disconnect();
    this.connectionPromise = null;
  }

  get isConnected() {
    try {
      return Boolean(this.socket);
    } catch (err) {
      return false;
    }
  }

  private connectionPromise: Promise<void> | null = null;

  /** Connects to pigeon's websocket server. Waits for connection to resolve */
  async connectToSocket(onDisconnect?: (reason: string) => void) {
    // Check if the socket is connected
    if (this.socket?.connected) {
      return;
    }

    // Someone else is already connecting, send that connection promise
    if (this.connectionPromise) {
      await this.connectionPromise;
      return;
    }

    // The same socket is reused for the whole page (INS-1391), so drop the previous
    // set of lifecycle handlers before adding these, or onDisconnect fires N times.
    this.socket?.off('connect');
    this.socket?.off('connect_error');
    this.socket?.off('exception');
    this.socket?.off('disconnect');

    // INS-1391: one Socket/Manager per page. Reconnect the existing socket instead of
    // creating another manager whose stale listeners and buffer outlive it.
    if (!this.socket) {
      this.socket = io(this.socketUrl, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        // ponytail: 1s connection timeout made every reconnect fail on mobile
        // (a websocket handshake rarely completes that fast), so the socket never
        // silently recovered mid-auth. 20s is the socket.io default.
        timeout: 20000,
        auth: (cb) => {
          void Promise.resolve(this.serviceToken?.()).then((serviceToken) =>
            cb({
              protocolVersion: PIGEON_PROTOCOL_VERSION,
              serviceToken: serviceToken ?? undefined,
            }),
          );
        },
      });
    } else if (!this.socket.active) {
      this.socket.connect();
    }

    this.connectionPromise = new Promise<void>((resolve, reject) => {
      this.socket?.on('connect', () => {
        resolve();
      });

      this.socket?.on('connect_error', (err) => {
        // Pigeon rejects stale clients at the handshake (INS-1391)
        reject(
          err.message === CLIENT_UPGRADE_REQUIRED
            ? new Error(CLIENT_UPGRADE_REQUIRED)
            : err,
        );
      });

      this.socket?.on('exception', (err) => {
        reject(err);
      });
    });

    this.socket?.on('disconnect', (reason) => {
      onDisconnect?.(reason);
      this.connectionPromise = null;
    });

    try {
      await this.connectionPromise;
    } catch (err) {
      // Reset so the next connectToSocket() builds a fresh connection instead
      // of re-awaiting this rejected promise forever (which would wedge every
      // retry after an initial connect_error until a full page reload).
      this.connectionPromise = null;
      throw err;
    }
  }

  /** Developer CLI only (needs the service token). */
  public startValidationAttempt = this.createTwoWaySocketMessage<
    StartValidationAttemptWsMessage,
    ClientValidationState & { capability?: string }
  >(WebsocketMessageType.GetOrCreateValidationState);

  public tryGetValidationAttempt = this.createTwoWaySocketMessage<
    GetValidationStateWsMessage,
    | (ClientValidationState & { notFound?: undefined })
    | { notFound: true }
    | null
  >(WebsocketMessageType.GetValidationState);

  /** Returns the validation's capability once; keep it for every later message. */
  public createValidationAttempt = this.createTwoWaySocketMessage<
    Omit<StartValidationAttemptWsMessage, 'submissionId'>,
    ClientValidationState & { capability: string }
  >(WebsocketMessageType.CreateValidationState);

  // INS-1391: one socket now lives for the whole page, so each handler replaces the
  // previous one instead of piling up on every reconnect or remount.
  onValidationExpired(action: () => void) {
    this.socket?.off(WebsocketMessageType.ValidationExpired);
    this.socket?.on(WebsocketMessageType.ValidationExpired, action);
  }

  onValidationData(action: (data: ClientValidationState) => void) {
    this.socket?.off(WebsocketMessageType.ValidationData);
    this.socket?.on(WebsocketMessageType.ValidationData, action);
  }

  onDemoValidationSuccess(action: () => void) {
    this.socket?.off(WebsocketMessageType.DemoValidationSuccess);
    this.socket?.on(WebsocketMessageType.DemoValidationSuccess, action);
  }

  public getCarrierForm = this.createTwoWaySocketMessage<
    GetCarrierLoginFormWsMessage,
    Form
  >(WebsocketMessageType.CarrierForm);

  public triggerManualParse = this.createTwoWaySocketMessage<
    TriggerManualParseWsMessage,
    { enqueued: boolean }
  >(WebsocketMessageType.TriggerManualParse);

  /**
   * INS-1391: send a validation command and wait for Pigeon's acknowledgement. Only
   * while connected (no buffered replay); retries reuse the same requestId so Pigeon
   * runs it at most once.
   */
  async sendValidationCommand(
    command: ValidationCommandWsMessage,
    // Longer than a dead owner's 15s browser lease, so a crashed Pigeon task is
    // retried past the point where another task can take the validation over
    retryForMs = 20_000,
    {
      /**
       * On a missed ack, reconnect before resending. Only for callers that
       * re-subscribe to their validation after a reconnect (Parrot does); the
       * developer CLI subscribes once, so a reconnect would leave it waiting.
       */
      reconnectOnTimeout = false,
    } = {},
  ): Promise<ValidationCommandAck> {
    const deadline = Date.now() + retryForMs;
    const retryable: ValidationCommandAck = {
      status: 'retryable',
      requestId: command.requestId,
    };
    for (;;) {
      const connected = reconnectOnTimeout
        ? await this.waitForConnection(deadline)
        : this.socket?.connected;
      if (!connected) throw new Error('Not connected to Pigeon');
      // A reconnect can land late in the window: never wait past the deadline
      const ackMs = reconnectOnTimeout
        ? Math.min(5000, deadline - Date.now())
        : 5000;
      if (ackMs <= 0) return retryable;
      let ack: ValidationCommandAck;
      try {
        ack = await this.socket!.timeout(ackMs).emitWithAck(
          WebsocketMessageType.ValidationCommand,
          command,
        );
      } catch {
        // No ack: the connection can be open but no longer delivering what we send
        // (staging, 2026-10-14: Pigeon only noticed via ping timeout ~50s later).
        // Resending on it is lost too, so drop the transport and let socket.io
        // reconnect; the retry below waits for the new connection. A wait the
        // deadline cut short says nothing about the socket, so keep it then.
        if (reconnectOnTimeout && ackMs === 5000) this.socket?.io.engine?.close();
        ack = retryable;
      }
      if (ack.status !== 'retryable' || Date.now() + 2000 > deadline)
        return ack;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }

  /**
   * True once the socket is connected, false if `deadline` passes first or the
   * socket isn't reconnecting (closed on purpose, or never opened).
   */
  private async waitForConnection(deadline: number) {
    // ponytail: polls instead of listening for 'connect', because connectToSocket()
    // replaces every 'connect' listener on each (re)connect and would drop ours
    while (!this.socket?.connected) {
      if (!this.socket?.active || Date.now() >= deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return true;
  }

  abortValidation(data: AbortValidationWsMessage) {
    if (this.socket?.connected) {
      this.socket?.emit(WebsocketMessageType.AbortValidation, data);
    }
  }

  subscribeToSubmissionUpdates = (
    submissionId: string,
    onSubmissionUpdate: () => void,
  ) => {
    const payload = { submissionId };

    this.socket?.emit(WebsocketMessageType.SubmissionSubscription, payload);

    this.socket?.on(
      WebsocketMessageTypeFactory.submissionData(submissionId),
      onSubmissionUpdate,
    );

    return () => {
      this.socket?.emit(WebsocketMessageType.UnsubscribeToSubmission, payload);
    };
  };

  unsubscribeToSubmissionUpdates = (submissionId: string) => {
    const payload = { submissionId };

    this.socket?.emit(WebsocketMessageType.UnsubscribeToSubmission, payload);
  };

  /**
   * Rerun a submission in pigeon if the submission data is still in its cache
   * @returns the new submission id
   * */
  async runAutomationForSubmission(submissionId: string) {
    const { data: newSubmissionId } = await this.client.post(
      `/automation/${submissionId}`,
    );

    return newSubmissionId;
  }

  async getCarriersImplementationData(): Promise<
    Array<CarrierImplementationData>
  > {
    const { data } = await this.client.get<Array<CarrierImplementationData>>(
      '/carriers',
    );
    return data;
  }

  async deleteSession(submissionId: string) {
    const accessToken = this.storage.getItem('access-token');
    await this.client.delete<void>(`/keep-alive/${submissionId}`, {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    });
  }

  async getBrowserSession(submissionId: string) {
    const accessToken = this.storage.getItem('access-token');
    const { data } = await this.client.get<CarrierPageStateResponse>(
      `/browser/${submissionId}`,
      {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return data;
  }

  async getActiveBrowserSessions(
    accessTokenEnv?: string,
  ): Promise<Array<CarrierPageStateResponse>> {
    const accessToken = this.storage.getItem(accessTokenEnv ?? 'access-token');
    const { data } = await this.client.get<CarrierPageStateResponse[]>(
      '/browser/',
      {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      },
    );
    return data;
  }

  async getKeepAliveJobs(accessTokenEnv?: string): Promise<KeepAliveResponse> {
    const accessToken = this.storage.getItem(accessTokenEnv ?? 'access-token');
    const { data } = await this.client.get<KeepAliveResponse>(`/keep-alive`, {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    });
    return data;
  }

  async signArtifactUrl(
    submissionId: string,
    name: string,
    type: PigeonS3ObjectType,
  ): Promise<PresignedPost> {
    const { data } = await this.client.post<
      { type: PigeonS3ObjectType; name: string },
      AxiosResponse<PresignedPost>
    >(`/artifact/${submissionId}`, {
      type,
      name,
    });
    return data;
  }

  setAccessToken(accessToken: string) {
    this.storage.setItem(PIGEON_ACCESS_TOKEN_STORAGE_KEY, accessToken);
  }

  async saveBrowserState(submissionId: string, pageState: CarrierPageState) {
    await this.client.post(`/browser/${submissionId}`, pageState);
  }

  async fetchComparionAgents(): Promise<{ data: ComparionAgent[] }> {
    return await this.client.get(`/browser/comparion-agents`);
  }

  /**
   * Get Browserbase debugger URL for a submission
   * Used for "Load in Browser" functionality from Slack
   */
  async getBrowserbaseDebuggerUrl(
    submissionId: string,
  ): Promise<string | null> {
    const { data } = await this.client.get<{ debuggerUrl: string | null }>(
      `/browserbase/debugger/${submissionId}`,
    );
    return data.debuggerUrl;
  }
}
